<script setup>
import { showModal, hideModal } from '@/Utils/modalBus'
import { ref, computed, getCurrentInstance, onMounted, onUnmounted } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import ConfirmModal from '@/components/modals/ConfirmModal.vue'
import MediaflowService from '@/services/MediaflowService'
import StreamStatsService from '@/services/StreamStatsService'
import { useNotification } from "@kyvg/vue3-notification"
import FormButton from '../Atoms/FormButton.vue'
import DropDownMenu from '../Atoms/DropDownMenu.vue'
import DropDownMenuItem from '../Atoms/DropDownMenuItem.vue'
import IconBase from '../icon/IconBase.vue'
import IconBin from '../icon/IconBin.vue'
import IconArrowRight from '../icon/IconArrowRight.vue'
import IconLinkChain from '../icon/IconLinkChain.vue'
import IconElipsisVertical from '../icon/IconElipsisVertical.vue'
import Badge from '../Badge.vue'
import StreamThumbnail from '../ui/StreamThumbnail.vue'
import StreamThumb from '../ui/StreamThumb.vue'
import StatusPill from '../Atoms/StatusPill.vue'
import Tooltip from '../Atoms/Tooltip.vue'
import IconStackedLayers from '../icon/IconStackedLayers.vue'
import IconCloud from '../icon/IconCloud.vue'
import { sessionWindow, formatDurationShort, formatClockTime } from './mediaflowMeta'

const { notify } = useNotification()

const store = useStore()
const router = useRouter()

const instance = getCurrentInstance();
const root = instance.proxy.$root;

const scopeAlive = ref(true)
const mediaPulse = ref(null)
const mediaPulseTimeoutCtrl = ref(null)
// Coarse clock for "live for 1h 12m" — a card only needs minute precision.
const now = ref(Date.now())
let clockTimer = null

const allInOneStreams = computed(() => store.getters['Streams/allInOneStreams'])
const shouldShowCompactModeStreams = computed(() => store.getters['Ui/shouldShowCompactModeStreams'])
const props = defineProps({
	mediaflow: {
		type: Object,
		default() { return {}; }
	},
	viewMode: {
		type: String,
		default: 'grid',
	},
})

const subscribeMediaPulse = async () => {
	(async function loop() {
		if (!scopeAlive.value) return;

		let timeoutInterval = 5000;
		let shouldUpdatePulse = props.mediaflow.enabled;
		if (document.hidden) {
			shouldUpdatePulse = false;
			timeoutInterval = 6000;
		}

		if (shouldUpdatePulse) {
			if (!document.hasFocus()) {
				timeoutInterval = 6000;
			}
		}
		if (shouldUpdatePulse) {
			try {
				const streamKey = props.mediaflow?.key;
				const pulseObject = await StreamStatsService.getStreamMediaPulse(
					streamKey
				);
				onMediaPulse(pulseObject);
			} catch (e) {
				console.log(e);
			}
		}
		mediaPulseTimeoutCtrl.value = setTimeout(loop, timeoutInterval);
	})();
}

const onMediaPulse = (pulseObject = {}) => {
	if (mediaPulse.value?.withoutPushStats) {
		const oldPushStats = mediaPulse.value.push_stats;
		pulseObject.push_stats = oldPushStats;
	}
	mediaPulse.value = pulseObject;
}

const unsubscribeMediaPulse = () => {
	clearTimeout(mediaPulseTimeoutCtrl.value);
}
onMounted(async () => {
	subscribeMediaPulse()
	clockTimer = setInterval(() => { now.value = Date.now() }, 30_000)
})
onUnmounted(() => {
	unsubscribeMediaPulse()
	if (clockTimer) clearInterval(clockTimer)
	scopeAlive.value = false
})

const onDeleteMediaflow = async () => {
	hideModal('delete-mediaflow-' + props.mediaflow._id);
	try {
		await MediaflowService.deleteMediaflow(props.mediaflow._id);
		store.dispatch('Mediaflows/removeMediaflow', { id: props.mediaflow._id });
		notify({ group: 'success', text: 'Cloud Production deleted' })
	} catch (err) {
		notify({ group: 'error', text: err.message || 'Cloud Production delete failed' })
	}
}

const showDeleteModal = (ev) => {
	ev?.preventDefault?.();
	ev?.stopPropagation?.();
	showModal('delete-mediaflow-' + props.mediaflow._id);
}

const destinationStream = computed(() => {
	if (!props.mediaflow.destination) return null;
	return allInOneStreams.value.find(stream => stream._id === props.mediaflow.destination);
})

const destinationLabel = computed(() => {
	if (!destinationStream.value) return 'No destination yet'
	return destinationStream.value.name.length > 40
		? destinationStream.value.name.slice(0, 40)
		: destinationStream.value.name
})

const flowsLabel = computed(() => {
	const count = props.mediaflow.flows?.length ?? 0
	return `${count} ${count === 1 ? 'Flow' : 'Flows'}`
})

const outputLabel = computed(() => {
	const res = props.mediaflow.screenResolution || props.mediaflow.resolutionPreset
	const fps = props.mediaflow.fps ? `${props.mediaflow.fps} fps` : null
	return [res ? String(res).replace(/\s+/g, '') : null, fps].filter(Boolean).join(' · ')
})

// offline · starting · live · disabled — the same StatusPill as the workspace.
const status = computed(() => {
	if (!props.mediaflow.enabled) return { key: 'disabled', label: 'Disabled', variant: 'muted', pulse: false }
	if (mediaPulse.value?.alive) return { key: 'live', label: 'Live', variant: 'success', pulse: true }
	if (props.mediaflow.obsInstance) return { key: 'starting', label: 'Starting', variant: 'warning', pulse: true }
	return { key: 'offline', label: 'Offline', variant: 'muted', pulse: false }
})

// Reserved session window straight from the list payload: how long it has
// been running on the server and when the server will stop it.
const session = computed(() => sessionWindow(props.mediaflow, now.value))
const sessionLabel = computed(() => {
	if (!session.value) return ''
	const running = `${status.value.key === 'live' ? 'live' : 'running'} for ${formatDurationShort(session.value.elapsedSec)}`
	return session.value.remainingSec > 0 ? `${running} · ${formatDurationShort(session.value.remainingSec)} left` : running
})
const sessionTitle = computed(() => (session.value
	? `Auto-stops at ${formatClockTime(session.value.endsAt)}.`
	: ''))

const toggleMediaflow = async (ev) => {
	ev?.preventDefault?.();
	ev?.stopPropagation?.();
	try {
		await MediaflowService.toggleMediaflow(props.mediaflow._id);
		store.dispatch('Mediaflows/toggleMediaflow', { id: props.mediaflow._id });
		notify({ group: "success", text: `Cloud Production ${props.mediaflow.enabled ? 'enabled' : 'disabled'}` });
	} catch (err) {
		notify({ group: "error", text: err.message || "Cloud Production source update failed" });
	}
};

const open = () => {
	router.push({
		name: 'MediaflowContent',
		params: { id: props.mediaflow._id },
		query: { sourceTab: 'livestream' },
	})
}

</script>

<template>
	<!-- LIST VIEW -->
	<div v-if="shouldShowCompactModeStreams"
		class="flex flex-wrap md:flex-nowrap gap-x-3 gap-y-1 items-center w-full relative rounded-xl group bg-surface-2 ring-1 ring-surface-3 transition-colors duration-200 hover:bg-surface-3 px-3 py-2 cursor-pointer"
		:data-status="status.key"
		@click="open">
		<div class="relative shrink-0 overflow-hidden rounded h-12 w-24 bg-gradient-to-b from-surface-darker to-black">
			<StreamThumb class="mediaflow-thumb w-full h-full" :stream="mediaflow" :media-pulse="mediaPulse" :compact-mode="true" />
		</div>

		<div class="flex min-w-0 flex-1 basis-0 md:basis-auto items-center gap-2">
			<div class="flex flex-col min-w-0 flex-1">
				<div class="flex items-center gap-2 min-w-0">
					<h3 class="truncate text-sm md:text-base text-light-1 font-medium m-0 min-w-0" :title="mediaflow.name">
						{{ mediaflow.name }}
					</h3>
					<Badge class="shrink-0">{{ flowsLabel }}</Badge>
				</div>
				<p class="text-tiny truncate flex items-center gap-1.5 m-0"
					:class="mediaflow.destination ? 'text-surface-8' : 'text-surface-6'">
					<icon-base class="shrink-0" :width="12" :height="12" width-view-box="14" height-view-box="14" fill="none">
						<icon-link-chain />
					</icon-base>
					<span class="truncate">{{ destinationLabel }}</span>
					<template v-if="sessionLabel">
						<span class="text-surface-6" aria-hidden="true">·</span>
						<span class="truncate" :class="status.key === 'live' ? 'text-success' : 'text-gold'" :title="sessionTitle">{{ sessionLabel }}</span>
					</template>
				</p>
			</div>
			<div class="shrink-0 md:hidden" @click.stop>
				<DropDownMenu isIcon position="right" type="secondary" no-border>
					<template #toggle-button>
						<icon-base class="!ml-1">
							<IconElipsisVertical />
						</icon-base>
					</template>
					<template #menu-items>
						<DropDownMenuItem :disabled="mediaflow.enabled && !!mediaPulse?.alive" @click="toggleMediaflow">
							{{ mediaflow.enabled ? 'Disable' : 'Enable' }}
						</DropDownMenuItem>
						<DropDownMenuItem :disabled="!!mediaPulse?.alive" @click="showDeleteModal">
							Delete
						</DropDownMenuItem>
					</template>
				</DropDownMenu>
			</div>
		</div>

		<StatusPill pill :variant="status.variant" :pulse="status.pulse" :title="sessionTitle || status.label" class="shrink-0" :data-status="status.key">
			{{ status.label }}
		</StatusPill>

		<div class="hidden md:flex items-center gap-2 shrink-0" @click.stop>
			<FormButton type="danger" size="sm" isIcon aria-label="Delete" :disabled="mediaPulse?.alive"
				@click="showDeleteModal">
				<icon-base>
					<icon-bin />
				</icon-base>
			</FormButton>
			<FormButton v-if="mediaflow.enabled" type="secondary" size="sm" :disabled="mediaPulse?.alive"
				@click="toggleMediaflow">
				Disable
			</FormButton>
			<FormButton v-else type="main" size="sm" @click="toggleMediaflow">
				Enable
			</FormButton>
		</div>

		<confirm-modal
			:modalId="'delete-mediaflow-' + mediaflow._id"
			title="Delete this production?"
			message="Its flows, sources and automation are removed. This action cannot be undone."
			ok-text="Delete"
			cancel-text="Cancel"
			modal-type="danger"
			@modal-confirm="onDeleteMediaflow"
		/>
	</div>

	<!-- GRID VIEW -->
	<div
		v-else
		class="flex relative group flex-col bg-surface-2 rounded-xl h-full ring-1 ring-surface-3 transition-[box-shadow,transform] duration-200 hover:ring-surface-4 focus-within:ring-surface-4"
		:data-status="status.key"
	>
		<router-link class="text-reset hover:no-underline flex flex-col h-full"
			:to="{ name: 'MediaflowContent', params: { id: mediaflow._id }, query: { sourceTab: 'livestream' } }">
			<div class="relative overflow-hidden rounded-t-xl bg-black">
				<StreamThumbnail
					:stream="mediaflow"
					:media-pulse="mediaPulse"
					:obs="true"
					:compact-mode="false"
				/>
				<div class="pointer-events-none absolute inset-x-0 top-0 z-10 flex items-start justify-between gap-2 p-2.5">
					<StatusPill pill :variant="status.variant" :pulse="status.pulse" :title="sessionTitle || status.label" class="!bg-black/60 backdrop-blur-sm">
						{{ status.label }}
					</StatusPill>
					<span v-if="outputLabel" class="rounded-full bg-black/60 px-2 py-0.5 text-xs font-medium tabular-nums text-surface-9 backdrop-blur-sm">
						{{ outputLabel }}
					</span>
				</div>
				<div
					class="pointer-events-none absolute inset-0 z-20 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
				>
					<span class="inline-flex items-center gap-1.5 text-sm font-medium text-white bg-surface-3/90 rounded-full px-3.5 py-1.5 shadow-lg">
						Open production
						<icon-base :width="14" :height="14" width-view-box="24" height-view-box="24" fill="none">
							<icon-arrow-right />
						</icon-base>
					</span>
				</div>
			</div>
			<div class="pt-3 px-3.5 flex flex-col flex-1 gap-1">
				<h2 class="text-base font-medium mb-0 truncate text-surface-text-2" :title="mediaflow.name">
					{{ mediaflow.name }}
				</h2>
				<p class="text-tiny mb-0 flex items-center min-w-0" :class="mediaflow.destination ? 'text-surface-text-1' : 'text-surface-8'">
					<icon-base class="mr-1.5 shrink-0 text-surface-8" :width="13" :height="13" width-view-box="14" height-view-box="14" fill="none">
						<icon-link-chain />
					</icon-base>
					<span class="truncate">{{ destinationLabel }}</span>
				</p>
				<Tooltip v-if="sessionLabel" :content="sessionTitle" wrapper-class="inline-flex min-w-0 max-w-full">
					<p class="text-tiny mb-0 flex items-center gap-1.5 min-w-0" :class="status.key === 'live' ? 'text-success' : 'text-gold'" data-testid="card-session">
						<icon-base class="shrink-0" :width="13" :height="13" fill="none"><icon-cloud /></icon-base>
						<span class="truncate">{{ sessionLabel }}</span>
					</p>
				</Tooltip>

				<div class="flex items-center justify-end gap-2 pb-3 mt-auto pt-3">
					<Badge class="inline-flex mr-auto items-center gap-1.5">
						<icon-base :width="12" :height="12" width-view-box="24" height-view-box="24" fill="none" class="text-surface-8">
							<icon-stacked-layers />
						</icon-base>
						{{ flowsLabel }}
					</Badge>
					<FormButton @click="showDeleteModal" type="danger" size="sm" isIcon aria-label="Delete" :disabled="mediaPulse?.alive"
						class="transition-opacity duration-200 lg:opacity-0 group-hover:opacity-100 focus:opacity-100">
						<icon-base>
							<icon-bin />
						</icon-base>
					</FormButton>
					<FormButton v-if="mediaflow.enabled" @click.prevent="toggleMediaflow" :disabled="mediaPulse?.alive" type="secondary" size="sm">
						Disable
					</FormButton>
					<FormButton v-else @click.prevent="toggleMediaflow" type="main" size="sm">
						Enable
					</FormButton>
				</div>
			</div>
		</router-link>
		<confirm-modal
			:modalId="'delete-mediaflow-' + mediaflow._id"
			title="Delete this production?"
			message="Its flows, sources and automation are removed. This action cannot be undone."
			ok-text="Delete"
			cancel-text="Cancel"
			modal-type="danger"
			@modal-confirm="onDeleteMediaflow"
		/>
	</div>
</template>

<style scoped>
.mediaflow-thumb :deep(#thumb-container) {
	aspect-ratio: auto !important;
	height: 100% !important;
}
.mediaflow-thumb :deep(#thumb-container) video,
.mediaflow-thumb :deep(#thumb-container) img {
	height: 100% !important;
	width: 100% !important;
	object-fit: cover !important;
}
</style>
